import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Button from 'components/Button';
import styles from './BoardTextColorModal.module.scss';

export interface BoardTextColorModalProps {
  show: boolean;
  onHide: () => void;
  initialTitleColor: string;
  initialSubtitleColor: string;
  onSave: (payload: { titleColor: string; subtitleColor: string }) => void;
  onResetToDefault: () => void;
}

function isValidHexColor(value: string): boolean {
  return /^#([0-9a-fA-F]{6})$/.test(value);
}

export default function BoardTextColorModal({
  show,
  onHide,
  initialTitleColor,
  initialSubtitleColor,
  onSave,
  onResetToDefault,
}: BoardTextColorModalProps) {
  const [titleColor, setTitleColor] = useState(initialTitleColor);
  const [subtitleColor, setSubtitleColor] = useState(initialSubtitleColor);

  useEffect(() => {
    if (!show) return;
    setTitleColor(
      isValidHexColor(initialTitleColor) ? initialTitleColor : '#333333',
    );
    setSubtitleColor(
      isValidHexColor(initialSubtitleColor) ? initialSubtitleColor : '#808080',
    );
  }, [show, initialTitleColor, initialSubtitleColor]);

  const handleApply = useCallback(() => {
    const nextTitle = isValidHexColor(titleColor) ? titleColor : '#333333';
    const nextSubtitle = isValidHexColor(subtitleColor)
      ? subtitleColor
      : '#808080';
    onSave({ titleColor: nextTitle, subtitleColor: nextSubtitle });
  }, [onSave, subtitleColor, titleColor]);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Change color of text</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.modalBody}>
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Kanban title</div>
            <div className={styles.colorRow}>
              <input
                type="color"
                value={titleColor}
                onChange={(e) => setTitleColor(e.target.value)}
                aria-label="Kanban title color"
                className={styles.colorInput}
              />
              <input
                type="text"
                value={titleColor}
                onChange={(e) => setTitleColor(e.target.value)}
                aria-label="Kanban title color hex"
                className={styles.colorTextInput}
              />
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>Kanban subtitle</div>
            <div className={styles.colorRow}>
              <input
                type="color"
                value={subtitleColor}
                onChange={(e) => setSubtitleColor(e.target.value)}
                aria-label="Kanban subtitle color"
                className={styles.colorInput}
              />
              <input
                type="text"
                value={subtitleColor}
                onChange={(e) => setSubtitleColor(e.target.value)}
                aria-label="Kanban subtitle color hex"
                className={styles.colorTextInput}
              />
            </div>
          </div>

          <div className={styles.preview}>
            <div className={styles.previewTitle} style={{ color: titleColor }}>
              Kanban Board Title
            </div>
            <div
              className={styles.previewSubtitle}
              style={{ color: subtitleColor }}
            >
              Drag and drop cards across lists
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onResetToDefault} type="button">
          Reset
        </Button>
        <Button variant="secondary" onClick={onHide} type="button">
          Cancel
        </Button>
        <Button variant="primary" onClick={handleApply} type="button">
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
